import React, { FC, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { PartitionModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import FormRow from '../../Common/Components/FormRow'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import Select, { SelectOption } from '../../Common/Components/Selects/Select/Select'
import { useFormContext, UseFormMethods, useWatch } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { PartitionModalTelSysLayout } from '../Utils/partitionModalTelSysLayout'
import {
    buildPartitionLicensedForSelectValues,
    shouldDisablePartitionLicensedForField
} from '../Constants/LicensedForRptUserTypeOptions'
import { getPartitionLicensedForSelectLabel } from '../Utils/partitionLicensedForLabels'
import { PartitionConfigurationForm } from '../Types/Types'
import { PARTITION_SYNC_TYPE } from '../Constants/Constants'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import {
    PARTITION_BILLING_TAG_FIELD_NAME,
    PARTITION_BILLED_MONTHLY_FIELD_NAME,
    PARTITION_COMMS_ENABLED_FIELD_NAME,
    PARTITION_DESCRIPTION_FIELD_NAME,
    PARTITION_DELETE_ACD_AGENTS_ON_DOWNGRADE_FIELD_NAME,
    PARTITION_IDENTIFIER_FIELD_NAME,
    PARTITION_ID_IN_TELSYS_FIELD_NAME,
    PARTITION_IS_GLOBAL_FIELD_NAME,
    PARTITION_LICENSED_FOR_FIELD_NAME,
    PARTITION_SYNC_TYPE_FIELD_NAME,
    PARTITION_TAGS_FIELD_NAME
} from '../Constants/FieldNames'

type Props = {
    isEditMode: boolean
    originalLicensedForRptUserType: string
    viewOnly?: boolean
    telSysLayout: PartitionModalTelSysLayout
}

/**
 * Determines whether the given licence level should show supervisor-oriented partition settings.
 *
 * @param licensedForRptUserType - Current licence value from the form.
 * @returns True when the legacy partition page would expose advanced ACD/supervisor options.
 */
const isAcdOrHigherLicence = (licensedForRptUserType: string): boolean =>
    [
        ReportingAccessLevels.ENTERPRISE,
        ReportingAccessLevels.CALL_CENTRE_AGENT,
        ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
    ].includes(licensedForRptUserType as ReportingAccessLevels)

/**
 * Details tab for partition configuration with TelSys-specific fields.
 *
 * @param props - Tab props.
 * @returns Details tab content.
 */
export const PartitionGeneralTab: FC<Props> = ({
    isEditMode,
    originalLicensedForRptUserType,
    viewOnly,
    telSysLayout
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    const { control, getValues, setValue }: UseFormMethods<PartitionConfigurationForm> =
        useFormContext<PartitionConfigurationForm>()
    const disabled: boolean = viewOnly ?? false
    const commonDevicesLabel: string = telSysLayout.detailsCommonDevicesPanasonicSuffix
        ? language.get(PartitionModal.PartitionModal_Details_CommonDevicesPanasonic)
        : language.get(PartitionModal.PartitionModal_Details_CommonDevices)

    const currentLicensedForRptUserType: string =
        useWatch({
            control,
            name: PARTITION_LICENSED_FOR_FIELD_NAME,
            defaultValue: ReportingAccessLevels.LITE
        }) ?? ReportingAccessLevels.LITE
    const previousLicensedForRef = useRef<string>(currentLicensedForRptUserType)

    const [licensedForAllOptionsRevealed, setLicensedForAllOptionsRevealed] = useState(false)

    const partIDInTelSysRaw: unknown = useWatch({
        control,
        name: PARTITION_ID_IN_TELSYS_FIELD_NAME,
        defaultValue: EMPTY_STRING
    })
    const identifier: string = String(
        useWatch({
            control,
            name: PARTITION_IDENTIFIER_FIELD_NAME,
            defaultValue: EMPTY_STRING
        }) ?? EMPTY_STRING
    )
    const isGlobal: boolean = !!useWatch({
        control,
        name: PARTITION_IS_GLOBAL_FIELD_NAME,
        defaultValue: false
    })
    const partIDInTelSysStr: string =
        typeof partIDInTelSysRaw === 'string' || typeof partIDInTelSysRaw === 'number'
            ? String(partIDInTelSysRaw)
            : EMPTY_STRING
    const showContainsCommonDevices: boolean = partIDInTelSysStr.trim() === '0'

    useEffect((): void => {
        if (!showContainsCommonDevices) {
            setValue(PARTITION_IS_GLOBAL_FIELD_NAME, false)
        }
    }, [setValue, showContainsCommonDevices])

    useEffect((): void => {
        if (
            previousLicensedForRef.current !== ReportingAccessLevels.ENTERPRISE &&
            currentLicensedForRptUserType === ReportingAccessLevels.ENTERPRISE
        ) {
            setValue(PARTITION_SYNC_TYPE_FIELD_NAME, PARTITION_SYNC_TYPE.ONE_TIME_FULL)
            setValue(PARTITION_BILLED_MONTHLY_FIELD_NAME, true)
        }

        previousLicensedForRef.current = currentLicensedForRptUserType
    }, [currentLicensedForRptUserType, setValue])

    useHotkeys(
        'ctrl+alt+/',
        (event: KeyboardEvent): void => {
            event.preventDefault()
            setLicensedForAllOptionsRevealed(true)
        },
        { enableOnFormTags: true, enabled: telSysLayout.detailsShowLicensedFor && !disabled },
        [disabled, telSysLayout.detailsShowLicensedFor]
    )

    const licensedForSelectOptions: SelectOption<string>[] = useMemo((): SelectOption<string>[] => {
        const orderedValues: string[] = buildPartitionLicensedForSelectValues(
            currentLicensedForRptUserType,
            isEditMode,
            licensedForAllOptionsRevealed
        )

        return orderedValues.map(
            (value: string): SelectOption<string> => ({
                value,
                label: getPartitionLicensedForSelectLabel(language, value)
            })
        )
    }, [currentLicensedForRptUserType, isEditMode, language, licensedForAllOptionsRevealed])

    const showLicensedFor: boolean = telSysLayout.detailsShowLicensedFor && !isGlobal
    const licensedForFieldDisabled: boolean = shouldDisablePartitionLicensedForField(
        disabled,
        currentLicensedForRptUserType,
        licensedForAllOptionsRevealed
    )
    const showBilledMonthly: boolean =
        telSysLayout.detailsShowBilledMonthly &&
        (currentLicensedForRptUserType === ReportingAccessLevels.ENTERPRISE ||
            getValues(PARTITION_BILLED_MONTHLY_FIELD_NAME) === true)
    const showDeleteAcdAgentsOnDowngrade: boolean =
        isEditMode &&
        isAcdOrHigherLicence(originalLicensedForRptUserType) &&
        !isAcdOrHigherLicence(currentLicensedForRptUserType)

    return (
        <div className='partition-config-tab'>
            <FormRow>
                <OutlinedTextInput
                    labelLeft={language.get(PartitionModal.PartitionModal_Details_Identifier)}
                    value={identifier}
                    placeholder={language.get(PartitionModal.PartitionModal_Details_IdentifierPlaceholder)}
                    disabled
                />
            </FormRow>
            <FormRow>
                <ValidationErrors name={PARTITION_DESCRIPTION_FIELD_NAME}>
                    <OutlinedTextInput
                        labelLeft={language.get(PartitionModal.PartitionModal_General_Description)}
                        inputName={PARTITION_DESCRIPTION_FIELD_NAME}
                        rules={{ required: language.get(PartitionModal.PartitionModal_General_DescriptionRequired) }}
                        disabled={disabled}
                    />
                </ValidationErrors>
            </FormRow>
            <FormRow>
                <ValidationErrors name={PARTITION_ID_IN_TELSYS_FIELD_NAME}>
                    <OutlinedTextInput
                        labelLeft={language.get(PartitionModal.PartitionModal_General_IDInTelSys)}
                        inputName={PARTITION_ID_IN_TELSYS_FIELD_NAME}
                        disabled={disabled || isEditMode}
                    />
                </ValidationErrors>
            </FormRow>
            <FormRow>
                <OutlinedTextInput
                    labelLeft={language.get(PartitionModal.PartitionModal_Details_BillingIdOrTag)}
                    inputName={PARTITION_BILLING_TAG_FIELD_NAME}
                    disabled={disabled}
                />
            </FormRow>
            {showBilledMonthly ? (
                <FormRow>
                    <RouterCheckbox
                        labelLeft={language.get(PartitionModal.PartitionModal_Details_BilledMonthly)}
                        name={PARTITION_BILLED_MONTHLY_FIELD_NAME}
                        disabled={disabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {telSysLayout.detailsShowTags ? (
                <FormRow>
                    <OutlinedTextInput
                        labelLeft={language.get(PartitionModal.PartitionModal_General_Tags)}
                        inputName={PARTITION_TAGS_FIELD_NAME}
                        disabled={disabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {showLicensedFor ? (
                <FormRow>
                    <ValidationErrors name={PARTITION_LICENSED_FOR_FIELD_NAME}>
                        <Select<string>
                            name={PARTITION_LICENSED_FOR_FIELD_NAME}
                            label={language.get(PartitionModal.PartitionModal_Details_LicensedFor)}
                            labelClass='label-left'
                            containerClassName='outlined-select'
                            popoverClassName='outlined-select-popper'
                            variant='outlined'
                            options={licensedForSelectOptions}
                            disabled={licensedForFieldDisabled}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            {showDeleteAcdAgentsOnDowngrade ? (
                <FormRow>
                    <RouterCheckbox
                        labelLeft={language.get(PartitionModal.PartitionModal_Details_DeleteAcdAgentsOnDowngrade)}
                        name={PARTITION_DELETE_ACD_AGENTS_ON_DOWNGRADE_FIELD_NAME}
                        disabled={disabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {showContainsCommonDevices ? (
                <FormRow>
                    <RouterCheckbox
                        labelLeft={commonDevicesLabel}
                        name={PARTITION_IS_GLOBAL_FIELD_NAME}
                        disabled={disabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            <FormRow>
                <RouterCheckbox
                    labelLeft={language.get(PartitionModal.PartitionModal_Details_CommunicationEnabled)}
                    name={PARTITION_COMMS_ENABLED_FIELD_NAME}
                    disabled={disabled}
                />
            </FormRow>
        </div>
    )
}
